import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { ConversationDTO, ConversationMode } from '@harness/shared';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, ExternalLink, FileText, Folder, Hammer, LayoutTemplate, Plus, Search as SearchIcon } from 'lucide-react';
import TopBar, { SearchButton } from '../../components/TopBar';
import { Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import { modeFromPath, modeMeta, MODES, modePath } from '../../lib/modes';
import { useConversations, useEnvironment, useIntegrations, useProject, useServer } from '../../lib/queries';
import { RoleContext } from '../../lib/role';
import { toast } from '../../lib/store';
import Chat from './Chat';
import ContextPanel, { MODE_TABS, type CtxTab } from './ContextPanel';
import FeatureDialogs from './FeatureDialogs';
import Sidebar from './Sidebar';
import WireframePanel from './WireframePanel';
import { ProjectPicker } from './TopBarPickers';

interface Layout {
  sideOpen: boolean;
  ctxOpen: boolean;
  ctxTab: CtxTab;
}

function useViewport() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

export default function Workspace() {
  const { slug, convId, mode: pathMode } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { project, isLoading, error } = useProject(slug);
  const server = useServer(project?.id);
  const environment = useEnvironment(project?.id);
  const convs = useConversations(project?.id);
  const integrations = useIntegrations(project?.id);
  // The tab: the open conversation's mode, else the URL's (/p/<slug>/tickets), else Features.
  const openConv = convs.data?.find((c) => c.id === convId);
  const mode: ConversationMode = openConv?.mode ?? modeFromPath(pathMode) ?? 'feature';
  const jiraLinked = !!integrations.data?.jira;
  // Each mode keeps its own side-panel tab (Features' is saved with the layout).
  const [modeTab, setModeTab] = useState<Partial<Record<ConversationMode, CtxTab>>>({});
  const width = useViewport();
  const narrow = width < 1200;
  const mobile = width < 900;

  // Panel layout per project: restored from the project's saved UI state, else viewport defaults.
  const [layout, setLayout] = useState<Layout | null>(null);
  const userSet = useRef({ side: false, ctx: false });
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!project?.id || loadedFor.current === project.id) return;
    loadedFor.current = project.id;
    const saved = (project.uiState ?? {}) as Partial<Layout>;
    userSet.current = { side: saved.sideOpen !== undefined, ctx: saved.ctxOpen !== undefined };
    setLayout({
      sideOpen: mobile ? false : (saved.sideOpen ?? width >= 900),
      ctxOpen: mobile ? false : width < 1200 && (saved.sideOpen ?? width >= 900) ? false : (saved.ctxOpen ?? width >= 1200),
      ctxTab: saved.ctxTab ?? 'tasks',
    });
  }, [project?.id, project?.uiState, width, mobile]);

  // Viewport defaults apply until the user toggles a pane manually.
  useEffect(() => {
    setLayout((l) => {
      if (!l) return l;
      let next = l;
      if (width < 1200 && l.ctxOpen && !userSet.current.ctx) next = { ...next, ctxOpen: false };
      if (width < 900 && l.sideOpen && !userSet.current.side) next = { ...next, sideOpen: false };
      return next;
    });
  }, [width]);

  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const updateLayout = useCallback(
    (patch: Partial<Layout>, manual?: 'side' | 'ctx') => {
      if (manual) userSet.current[manual] = true;
      setLayout((l) => {
        const next = { ...(l as Layout), ...patch };
        // Under 1200px only one side card is open at a time (v2).
        if (window.innerWidth < 1200) {
          if (patch.sideOpen && next.ctxOpen) next.ctxOpen = false;
          else if (patch.ctxOpen && next.sideOpen) next.sideOpen = false;
        }
        clearTimeout(saveTimer.current);
        if (project?.id && !mobile)
          saveTimer.current = setTimeout(() => void api.patch(`/api/projects/${project.id}`, { uiState: next }).catch(() => {}), 800);
        return next;
      });
    },
    [project?.id, mobile],
  );

  // Pick-up-where-you-left-off: remember the open Features conversation on the project.
  useEffect(() => {
    if (!project?.id || !convId || !openConv || openConv.mode !== 'feature') return;
    if (project.lastConversationId !== convId) {
      void api.patch(`/api/projects/${project.id}`, { lastConversationId: convId, touch: true }).then(() => void qc.invalidateQueries({ queryKey: qk.projects }));
    }
  }, [project?.id, convId, openConv, project?.lastConversationId, qc]);

  // Context-panel tab requests from elsewhere (palette "Start feature", gates).
  useEffect(() => {
    const on = (e: Event) => updateLayout({ ctxOpen: true, ctxTab: (e as CustomEvent<CtxTab>).detail });
    window.addEventListener('harness:ctx-tab', on);
    return () => window.removeEventListener('harness:ctx-tab', on);
  }, [updateLayout]);

  const newConversation = useCallback(async () => {
    if (!project) return;
    try {
      const c = await api.post<ConversationDTO>(`/api/projects/${project.id}/conversations`, { mode });
      qc.setQueryData<ConversationDTO[]>(qk.conversations(project.id), (l) => [c, ...(l ?? []).filter((x) => x.id !== c.id)]);
      nav(`/p/${project.slug}/c/${c.id}`);
      if (mobile) updateLayout({ sideOpen: false });
      setTimeout(() => document.getElementById('composer')?.focus(), 60);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  }, [project, qc, nav, mobile, updateLayout, mode]);

  useEffect(() => {
    const on = () => void newConversation();
    window.addEventListener('harness:new-conversation', on);
    return () => window.removeEventListener('harness:new-conversation', on);
  }, [newConversation]);

  if (error) return <Navigate to="/" replace />;
  if (isLoading || !project || !layout)
    return (
      <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );

  // Unknown /p/<slug>/<x>: back to the project.
  if (pathMode && !modeFromPath(pathMode)) return <Navigate to={`/p/${project.slug}`} replace />;
  // No conversation in the URL: restore the tab's last one (Features), else its newest.
  if (!convId && convs.data && !(mode === 'ticket' && !jiraLinked)) {
    const mine = convs.data.filter((c) => c.mode === mode);
    const target = (mode === 'feature' ? mine.find((c) => c.id === project.lastConversationId) : undefined) ?? mine[0];
    if (target) return <Navigate to={`/p/${project.slug}/c/${target.id}`} replace />;
  }
  // The side panel's tabs depend on the mode; fall back to the mode's first tab.
  const wanted = mode === 'feature' ? layout.ctxTab : modeTab[mode];
  const ctxTab: CtxTab = wanted && MODE_TABS[mode].some(([k]) => k === wanted) ? wanted : MODE_TABS[mode][0]![0];
  const setCtxTab = (t: CtxTab) => (mode === 'feature' ? updateLayout({ ctxTab: t }) : setModeTab((m) => ({ ...m, [mode]: t })));
  const locked = mode === 'ticket' && integrations.data && !jiraLinked;

  // Wireframes get a wide preview beside the chat.
  // Create Wireframes: no sidebar and no panel toggle; the chat keeps a fixed width and the preview
  // takes all the remaining space.
  const wireframe = mode === 'wireframe';
  const sideOpen = !wireframe && layout.sideOpen;
  const ctxOpen = wireframe ? !mobile || layout.ctxOpen : layout.ctxOpen;
  const cols = mobile
    ? 'minmax(0,1fr)'
    : wireframe
      ? 'clamp(360px, 30vw, 480px) minmax(0,1fr)'
      : `${sideOpen ? '272px ' : ''}minmax(0,1fr)${ctxOpen ? (narrow ? ' 320px' : ' 360px') : ''}`;
  const closeDrawers = () => updateLayout({ sideOpen: false, ctxOpen: false });

  return (
    <RoleContext.Provider value={project.role ?? 'viewer'}>
    <div className="screen">
      <TopBar
        onToggleSidebar={wireframe ? undefined : () => updateLayout({ sideOpen: !layout.sideOpen }, 'side')}
        left={
          <span className="hide-narrow topbar-group">
            <button className="btn btn-secondary" onClick={() => nav('/')}>
              <Folder className="icon" style={{ fontSize: 15 }} />
              Projects
            </button>
            <SearchButton />
          </span>
        }
        right={
          <>
            {environment.data?.environment && <Link className="tag tag-neutral" to={`/p/${project.slug}/settings/hosting`} title="View hosting status">{environment.data.environment.state.replaceAll('_', ' ')}</Link>}
            {server.data?.connection && (!environment.data?.environment || environment.data.environment.state === 'ready') && (
              <a
                className="btn btn-secondary hide-narrow"
                href={`/api/projects/${project.id}/preview`}
                target="_blank"
                rel="noopener noreferrer"
                title={server.data.connection.mode === 'daytona' ? 'Open the site running in Daytona' : 'Open the project files in a new tab'}
              >
                <ExternalLink className="icon" style={{ fontSize: 15 }} />
                {environment.data?.environment ? 'Open preview' : 'View project'}
              </a>
            )}
            <ProjectPicker project={project} />
          </>
        }
        settings={`/p/${project.slug}/settings/agents`}
      />
      <div className="ws" style={{ gridTemplateColumns: cols }}>
        {sideOpen && (
          <>
            {mobile && <div className="drawer-backdrop" onClick={closeDrawers} />}
            <Sidebar
              project={project}
              mode={mode}
              activeConvId={convId}
              onNewConversation={newConversation}
              drawer={mobile}
              onNavigate={() => mobile && updateLayout({ sideOpen: false })}
              disabled={!!locked}
            />
          </>
        )}
        <div className="ws-center">
          {wireframe ? (
            <div className="row" style={{ flexWrap: 'nowrap', gap: 8 }}>
              <ModeTabs slug={project.slug} mode={mode} />
              {/* No sidebar here: switch between wireframes, or start a new one, from this row. */}
              <select
                className="input"
                style={{ flex: 1, minWidth: 0 }}
                value={convId ?? ''}
                onChange={(e) => e.target.value && nav(`/p/${project.slug}/c/${e.target.value}`)}
                aria-label="Your wireframes"
              >
                {!convId && <option value="">New wireframe</option>}
                {(convs.data ?? [])
                  .filter((c) => c.mode === 'wireframe')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
              </select>
              <button className="btn btn-icon btn-primary" title="New wireframe" aria-label="New wireframe" onClick={() => void newConversation()} disabled={project.role === 'viewer'}>
                <Plus className="icon" />
              </button>
            </div>
          ) : (
            <ModeTabs slug={project.slug} mode={mode} />
          )}
          {locked ? (
            <main className="card ws-card">
              <div className="mode-locked">
                <div style={{ maxWidth: 460 }}>
                  <div className="title-900" style={{ fontSize: 21, marginBottom: 6 }}>
                    Tickets needs Jira
                  </div>
                  <div className="muted" style={{ marginBottom: 16 }}>
                    Jira must be integrated with this project before the Researcher can draft tickets and create them on your board.
                  </div>
                  <Link className="btn btn-primary" to={`/p/${project.slug}/settings/integrations`}>
                    Connect Jira
                  </Link>
                </div>
              </div>
            </main>
          ) : (
            <Chat
              key={convId ?? `none-${mode}`}
              project={project}
              mode={mode}
              convId={convId}
              narrow={narrow}
              onToggleCtx={() => updateLayout({ ctxOpen: !layout.ctxOpen }, 'ctx')}
              onOpenCtx={(tab) => {
                updateLayout({ ctxOpen: true });
                setCtxTab(tab);
              }}
              onNewConversation={newConversation}
            />
          )}
        </div>
        {ctxOpen && (
          <>
            {mobile && <div className="drawer-backdrop" onClick={closeDrawers} />}
            {mode === 'wireframe' ? (
              <WireframePanel project={project} convId={convId} drawer={mobile} />
            ) : (
              <ContextPanel project={project} mode={mode} convId={convId} tab={ctxTab} onTab={setCtxTab} drawer={mobile} />
            )}
          </>
        )}
        <FeatureDialogs project={project} />
      </div>
    </div>
    </RoleContext.Provider>
  );
}

const MODE_ICON = { ticket: FileText, wireframe: LayoutTemplate, feature: Hammer, analysis: SearchIcon } as const;

/** Create Tickets · Create Wireframes · Build Features · Perform Analysis, above the chat. Beside the wide
 * wireframe preview they collapse into a dropdown. */
function ModeTabs({ slug, mode }: { slug: string; mode: ConversationMode }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (mode === 'wireframe') {
    const Current = MODE_ICON[mode];
    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button className="dropdown-trigger mode-select" aria-label={`Workspace mode: ${modeMeta(mode).label}. Switch mode`}>
            <Current className="icon" />
            <span className="label">{modeMeta(mode).label}</span>
            <ChevronDown className="icon faint" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="menu" align="start" sideOffset={8} style={{ width: 240 }}>
            {MODES.map((m) => {
              const Icon = MODE_ICON[m.mode];
              return (
                <button
                  key={m.mode}
                  className="menu-item"
                  data-active={m.mode === mode}
                  onClick={() => {
                    setOpen(false);
                    if (m.mode !== mode) nav(modePath(slug, m.mode));
                  }}
                >
                  <Icon className="icon" />
                  <span style={{ flex: 1 }}>{m.label}</span>
                </button>
              );
            })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  }
  return (
    <div className="mode-tabs" role="tablist" aria-label="Workspace mode">
      {MODES.map((m) => {
        const Icon = MODE_ICON[m.mode];
        return (
          <button key={m.mode} role="tab" aria-selected={mode === m.mode} className="mode-tab" onClick={() => mode !== m.mode && nav(modePath(slug, m.mode))}>
            <Icon className="icon" />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}
